import React, { useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { User, Save, Users, Search } from 'lucide-react';
import CustomerManager from './CustomerManager';
import toast from 'react-hot-toast';
import { customersApi } from '../services/api';

const CustomerInfo = ({ customerInfo, setCustomerInfo, companyId, onCustomerSaved }) => {
  const [isCustomerManagerOpen, setIsCustomerManagerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomerSelect = (customer) => {
    setCustomerInfo({
      company: customer.company,
      name: customer.name,
      address: customer.address,
      city: customer.city,
      phone: customer.phone,
      email: customer.email,
      id: customer.id
    });
    setIsCustomerManagerOpen(false);
  };

  const handleSaveCustomer = async () => {
    if (!customerInfo.company || !customerInfo.name || !customerInfo.email) {
      toast.error('❌ Bitte geben Sie Firmenname, Ansprechpartner und E-Mail ein');
      return;
    }

    setIsSaving(true);
    try {
      const customerData = {
        company: customerInfo.company,
        name: customerInfo.name,
        address: customerInfo.address || '',
        city: customerInfo.city || '',
        phone: customerInfo.phone || '',
        email: customerInfo.email || '',
        companyId: companyId
      };

      let savedCustomer;
      if (customerInfo.id) {
        // Update existing customer
        savedCustomer = await customersApi.update(customerInfo.id, customerData);
        toast.success(`✅ ${customerInfo.company} wurde erfolgreich aktualisiert`);
      } else {
        // Create new customer
        savedCustomer = await customersApi.create(customerData);
        // Update local state with new ID
        setCustomerInfo(prev => ({
          ...prev,
          id: savedCustomer.id
        }));
        toast.success(`✅ ${customerInfo.company} wurde erfolgreich erstellt`);
      }

      // Notify parent component
      if (onCustomerSaved) {
        onCustomerSaved(savedCustomer);
      }

    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('❌ Kunde konnte nicht gespeichert werden');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    setCustomerInfo({
      company: '',
      name: '',
      address: '',
      city: '',
      phone: '',
      email: ''
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-800">Kundeninformationen</h2>
          </div>
          <div className="flex gap-2">
            <Dialog open={isCustomerManagerOpen} onOpenChange={setIsCustomerManagerOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Kunde auswählen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Kunde auswählen</DialogTitle>
                </DialogHeader>
                <CustomerManager 
                  companyId={companyId}
                  onCustomerSelect={handleCustomerSelect}
                />
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={handleClear}>
              Felder löschen
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="customer-company">Firmenname</Label>
              <Input
                id="customer-company"
                value={customerInfo.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Musterkunde GmbH"
              />
            </div>

            <div>
              <Label htmlFor="customer-name">Ansprechpartner</Label>
              <Input
                id="customer-name"
                value={customerInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Max Mustermann"
              />
            </div>

            <div>
              <Label htmlFor="customer-address">Adresse</Label>
              <Input
                id="customer-address"
                value={customerInfo.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Kundenstraße 456"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="customer-city">PLZ & Ort</Label>
              <Input
                id="customer-city"
                value={customerInfo.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="54321 Kundenstadt"
              />
            </div>

            <div>
              <Label htmlFor="customer-phone">Telefon</Label>
              <Input
                id="customer-phone"
                value={customerInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+49 987 654321"
              />
            </div>

            <div>
              <Label htmlFor="customer-email">E-Mail *</Label>
              <Input
                id="customer-email"
                type="email"
                value={customerInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="max@musterkunde.de"
                required
              />
            </div>
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Tipp:</h4>
          <p className="text-blue-700 text-sm">
            Verwenden Sie "Kunde auswählen", um einen bereits erfassten Kunden zu laden, 
            oder erstellen Sie einen neuen Kunden direkt hier. Die Informationen werden 
            automatisch in das PDF-Angebot übernommen.
          </p>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button 
          className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
          onClick={handleSaveCustomer}
          disabled={isSaving}
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Speichere...' : 'Kundendaten speichern'}
        </Button>
      </div>
    </div>
  );
};

export default CustomerInfo;